import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BsWhatsapp, BsSend, BsPaperclip, BsArrowRepeat, BsFileEarmarkText, BsX } from 'react-icons/bs';
import { toast } from 'react-toastify';
import { getChatBetweenUsers, sendWhatsappMessage } from '../../../services/whatsapp';
import { formatDate, formatDateTime } from '../../../utils/FormatDateTime';
import { sanitizeMobile } from '../../../utils/phone';
import CustomLoading from '../../customLoading/CustomLoading';

const getMediaType = (mimeType = '') => {
    if (mimeType.startsWith('image/')) return 'image';
    if (mimeType.startsWith('video/')) return 'video';
    if (mimeType.startsWith('audio/')) return 'audio';
    return 'document';
};

const formatMessageText = (text) => {
    if (!text) return '';
    const withLinks = text.replace(
        /(https?:\/\/[^\s]+)/g,
        (url) => `<a href="${url}" target="_blank" rel="noopener noreferrer" class="underline">${url}</a>`
    );
    return withLinks.replace(/\n/g, '<br>');
};

const groupMessagesByDate = (messages) => {
    const groups = {};
    messages.forEach((message) => {
        const date = formatDate(message.created_at);
        if (!groups[date]) groups[date] = [];
        groups[date].push(message);
    });
    return groups;
};

const MediaPreview = ({ msg }) => {
    if (!msg.media_file) return null;
    const fileExtension = msg.media_file.split('.').pop()?.toLowerCase();
    const fileName = msg.file_name || msg.media_file.split('/').pop();

    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(fileExtension)) {
        return (
            <a href={msg.media_file} target="_blank" rel="noopener noreferrer" className="block mb-2">
                <img src={msg.media_file} alt={fileName} className="rounded-lg max-h-48 w-full object-contain border border-black/5" />
            </a>
        );
    }
    if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm'].includes(fileExtension)) {
        return (
            <video src={msg.media_file} controls className="rounded-lg max-h-48 w-full object-contain mb-2" />
        );
    }
    if (['mp3', 'wav', 'ogg', 'aac', 'm4a'].includes(fileExtension)) {
        return <audio src={msg.media_file} controls className="w-full mb-2" />;
    }
    return (
        <a href={msg.media_file} target="_blank" rel="noopener noreferrer" className={`flex items-center gap-2 rounded-lg p-2 mb-2 ${msg.sent ? 'bg-white/20' : 'bg-gray-100'}`}>
            <BsFileEarmarkText className="shrink-0" />
            <span className="text-xs truncate">{fileName}</span>
        </a>
    );
};

const LeadWhatsappChat = ({ phone }) => {
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    const [sending, setSending] = useState(false);
    const [text, setText] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const bottomRef = useRef(null);
    const fileInputRef = useRef(null);

    const chatPhone = useMemo(() => {
        if (!phone) return '';
        const trimmed = String(phone).trim();
        if (trimmed.startsWith('+')) return trimmed;
        return `+91${sanitizeMobile(trimmed)}`;
    }, [phone]);

    const groupedMessages = useMemo(() => groupMessagesByDate(messages), [messages]);

    // WhatsApp only allows free-form replies within 24h of the customer's last inbound message.
    const WINDOW_MS = 24 * 60 * 60 * 1000;
    const lastInboundMsg = useMemo(() => {
        let found = null;
        messages.forEach((m) => {
            if (m.sent) return;
            if (!found || new Date(m.created_at) > new Date(found.created_at)) found = m;
        });
        return found;
    }, [messages]);
    const lastInboundAt = lastInboundMsg ? new Date(lastInboundMsg.created_at).getTime() : null;

    const [nowTs, setNowTs] = useState(() => Date.now());
    useEffect(() => {
        const id = setInterval(() => setNowTs(Date.now()), 60 * 1000);
        return () => clearInterval(id);
    }, []);

    const hoursSinceInbound =
        lastInboundAt != null && !Number.isNaN(lastInboundAt)
            ? Math.floor((nowTs - lastInboundAt) / (60 * 60 * 1000))
            : null;
    const windowClosed =
        !loading &&
        (lastInboundAt == null || Number.isNaN(lastInboundAt) || nowTs - lastInboundAt > WINDOW_MS);

    useEffect(() => {
        if (chatPhone) fetchChats();
    }, [chatPhone]);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const fetchChats = async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const res = await getChatBetweenUsers(chatPhone);
            if (res.status === 200) {
                setMessages(Array.isArray(res.data) ? res.data : (res.data?.results || []));
                setFailed(false);
            } else {
                setFailed(true);
            }
        } catch (error) {
            console.error('Error fetching WhatsApp chat:', error);
            setFailed(true);
        } finally {
            setLoading(false);
        }
    };

    const handleSend = async () => {
        if (!text.trim() && !selectedFile) return;
        if (windowClosed) {
            toast.error("Can't send — the 24-hour window has closed. Ask the customer to message first.");
            return;
        }
        setSending(true);
        try {
            const formData = new FormData();
            formData.append('to', chatPhone);
            formData.append('message', text || '');
            if (selectedFile) {
                formData.append('file', selectedFile);
                formData.append('media_type', getMediaType(selectedFile.type));
            }
            const res = await sendWhatsappMessage(formData);
            if (res.status === 200) {
                setText('');
                setSelectedFile(null);
                if (fileInputRef.current) fileInputRef.current.value = '';
                fetchChats(true);
            } else {
                toast.error(res.data?.message || 'Unable to send message');
            }
        } catch (error) {
            console.error('Error sending WhatsApp message:', error);
            toast.error(error.response?.data?.message || 'Failed to send message');
        } finally {
            setSending(false);
        }
    };

    if (!chatPhone || failed) return null;

    return (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden flex flex-col">
            <div className="px-4 py-3 bg-green-50 border-b flex items-center justify-between gap-2 text-green-800 font-bold text-sm">
                <span className="flex items-center gap-2"><BsWhatsapp /> WhatsApp Chat</span>
                <button onClick={() => fetchChats()} className="text-green-700 hover:text-green-900 p-1 rounded-full hover:bg-green-100 transition-colors" title="Refresh">
                    <BsArrowRepeat size={14} />
                </button>
            </div>

            <div className="max-h-[360px] overflow-y-auto bg-[#e5ddd5]/30 p-3">
                {loading ? (
                    <div className="py-8 flex items-center justify-center">
                        <CustomLoading size={30} />
                    </div>
                ) : messages.length === 0 ? (
                    <div className="text-center py-8 text-[11px] text-gray-400 italic">No WhatsApp messages yet.</div>
                ) : (
                    Object.entries(groupedMessages).map(([date, msgsForDate]) => (
                        <div key={date}>
                            <div className="flex items-center my-2 text-gray-500">
                                <div className="flex-1 border-t border-gray-200" />
                                <span className="mx-2 bg-white px-2 py-0.5 rounded-full text-[10px] shadow-sm">{date}</span>
                                <div className="flex-1 border-t border-gray-200" />
                            </div>
                            {msgsForDate.map((msg, idx) => (
                                <div key={`${msg.id}-${idx}`} className={`mb-2 flex ${msg.sent ? 'justify-end' : 'justify-start'}`}>
                                    <div
                                        className={`max-w-[75%] px-3 py-2 rounded-2xl text-xs shadow-sm ${msg.sent
                                            ? 'bg-green-500 text-white rounded-tr-sm'
                                            : 'bg-white text-gray-800 rounded-tl-sm border border-gray-100'
                                            }`}
                                    >
                                        <MediaPreview msg={msg} />
                                        {msg.message_text && (
                                            <span dangerouslySetInnerHTML={{ __html: formatMessageText(msg.message_text) }} />
                                        )}
                                        <div className={`text-[9px] mt-1 text-right ${msg.sent ? 'text-white/70' : 'text-gray-400'}`}>
                                            {formatDateTime(msg.created_at).split(' ').slice(-2).join(' ')}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ))
                )}
                <div ref={bottomRef} />
            </div>

            <div className="border-t p-2 bg-white">
                {windowClosed && (
                    <div className="mb-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
                        <p className="font-bold">⚠️ 24-hour reply window closed</p>
                        <p className="mt-0.5">
                            {lastInboundMsg ? (
                                <>
                                    Customer last messaged on{' '}
                                    <span className="font-semibold">{formatDateTime(lastInboundMsg.created_at)}</span>
                                    {hoursSinceInbound != null ? ` (${hoursSinceInbound} hrs ago)` : ''}.{' '}
                                </>
                            ) : (
                                <>No incoming message from this customer yet. </>
                            )}
                            You can&apos;t send a message now — please request the customer to send a message first.
                        </p>
                    </div>
                )}
                {selectedFile && (
                    <div className="flex items-center gap-2 mb-2 bg-gray-50 border rounded-lg px-2 py-1 text-[11px] text-gray-600">
                        <span className="truncate flex-1">{selectedFile.name}</span>
                        <button onClick={() => { setSelectedFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }} className="text-gray-400 hover:text-gray-600">
                            <BsX size={16} />
                        </button>
                    </div>
                )}
                <div className="flex items-center gap-2">
                    <input
                        ref={fileInputRef}
                        type="file"
                        className="hidden"
                        id="lead-whatsapp-file"
                        onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                    />
                    <button
                        onClick={() => fileInputRef.current?.click()}
                        disabled={windowClosed}
                        className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors shrink-0 disabled:opacity-40 disabled:hover:bg-transparent"
                        title="Attach file"
                    >
                        <BsPaperclip size={16} />
                    </button>
                    <input
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                        placeholder={windowClosed ? 'Waiting for customer to message first…' : 'Type a message...'}
                        disabled={windowClosed}
                        className="flex-1 border border-gray-300 rounded-full px-3 py-2 text-xs outline-none focus:ring-2 focus:ring-primary/20 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    />
                    <button
                        onClick={handleSend}
                        disabled={sending || windowClosed || (!text.trim() && !selectedFile)}
                        className="p-2 bg-green-500 text-white rounded-full disabled:bg-gray-300 hover:bg-green-600 transition-colors shrink-0"
                        title="Send"
                    >
                        <BsSend size={14} />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default LeadWhatsappChat;
